import { useEffect, useState } from "react";
import emailjs from "@emailjs/browser";
import { toast } from "react-toastify";
import "./WorkWithUs.scss";

interface JobOpening {
  id: string;
  title: string;
  summary: string;
  responsibilities: string[];
  requirements: string[];
  differentials: string[];
  closing: string;
}

interface ApplicationForm {
  name: string;
  email: string;
  phone: string;
  city: string;
  experience: string;
  message: string;
}

const openings: JobOpening[] = [
  {
    id: "massoterapeuta",
    title: "Massoterapeuta Clínica e Integrativa",
    summary:
      "A Sodalita busca profissionais de massoterapia clínica e integrativa para atuar em atendimentos voltados ao ambiente corporativo, promovendo saúde física e equilíbrio emocional nos colaboradores.",
    responsibilities: [
      "Realizar atendimentos de massoterapia em empresas e/ou domicílio",
      "Aplicar técnicas terapêuticas conforme a necessidade de cada cliente",
      "Contribuir para a redução de dores, tensões e estresse",
      "Garantir um atendimento humanizado, ético e de alta qualidade",
    ],
    requirements: [
      "Formação em Massoterapia",
      "Conhecimento em técnicas clínicas e integrativas",
      "Boa comunicação e postura profissional",
      "Experiência com atendimento ao público (diferencial)",
    ],
    differentials: [
      "Experiência em ambiente corporativo",
      "Formação complementar em terapias integrativas",
    ],
    closing:
      "Se você busca atuar com propósito e impacto real na saúde das pessoas, venha fazer parte da Sodalita.",
  },
  {
    id: "sdr",
    title: "SDR (Sales Development Representative)",
    summary:
      "A Sodalita está em busca de um(a) SDR para atuar na prospecção de empresas e geração de oportunidades comerciais para nossas soluções de saúde corporativa.",
    responsibilities: [
      "Prospectar e qualificar leads (Outbound e Inbound)",
      "Realizar contato inicial com empresas (e-mail, telefone, LinkedIn)",
      "Agendar reuniões para o time comercial",
      "Alimentar e manter o CRM atualizado",
      "Identificar oportunidades e necessidades dos clientes",
    ],
    requirements: [
      "Boa comunicação verbal e escrita",
      "Organização e disciplina com metas",
      "Perfil proativo e orientado a resultados",
      "Interesse na área comercial B2B",
    ],
    differentials: [
      "Experiência prévia como SDR ou vendas consultivas",
      "Conhecimento em CRM (HubSpot, Pipedrive, etc.)",
      "Vivência com vendas para empresas",
    ],
    closing:
      "Se você é comunicativo(a), gosta de desafios e quer crescer na área comercial, a Sodalita é o seu lugar.",
  },
];

const emptyForm: ApplicationForm = {
  name: "",
  email: "",
  phone: "",
  city: "",
  experience: "",
  message: "",
};

const emailIsValid = (email: string) =>
  !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const WorkWithUs = () => {
  const [selectedJob, setSelectedJob] = useState<JobOpening | null>(null);
  const [formData, setFormData] = useState<ApplicationForm>(emptyForm);
  const [isSending, setIsSending] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    if (selectedJob) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedJob]);

  const openApplication = (job: JobOpening) => {
    setSelectedJob(job);
    setFormData(emptyForm);
    setHasAttemptedSubmit(false);
  };

  const closeApplication = () => {
    setSelectedJob(null);
    setFormData(emptyForm);
    setHasAttemptedSubmit(false);
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 11);
    let value = digits;

    if (digits.length > 2) {
      value = `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    }
    if (digits.length > 7) {
      value = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }

    setFormData((current) => ({ ...current, phone: value }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedJob) return;

    setHasAttemptedSubmit(true);
    const phoneIsValid = formData.phone.replace(/\D/g, "").length === 11;
    const validEmail = emailIsValid(formData.email);

    if (
      !formData.name.trim() ||
      !formData.city.trim() ||
      !formData.experience.trim() ||
      !phoneIsValid ||
      !validEmail
    ) {
      toast.error("Revise os campos obrigatórios destacados antes de enviar.");
      return;
    }

    setIsSending(true);
    emailjs
      .send(
        "service_bbvydnd",
        "template_8y8vhfu",
        { ...formData, vacancy: selectedJob.title },
        "oz45ERdEct62lUvwM"
      )
      .then(() => {
        toast.success("Candidatura enviada com sucesso! Obrigado pelo interesse.");
        closeApplication();
      })
      .catch(() => {
        toast.error("Não foi possível enviar sua candidatura. Tente novamente em instantes.");
      })
      .finally(() => setIsSending(false));
  };

  return (
    <section className="work-with-us" id="workWithUs">
      <div className="work-with-us__intro" data-aos="fade-up">
        <span className="work-with-us__eyebrow">Faça parte da Sodalita</span>
        <h1>Trabalhe conosco</h1>
        <p>
          Encontre uma oportunidade para transformar a rotina das pessoas por meio do bem-estar.
        </p>
      </div>

      <div className="work-with-us__grid" data-aos="fade-up">
        {openings.map((job) => (
          <article className="job-card" key={job.id}>
            <div className="job-card__heading">
              <span className="job-card__tag">Vaga aberta</span>
              <h2>{job.title}</h2>
            </div>
            <p className="job-card__summary">{job.summary}</p>

            <div className="job-card__details">
              <JobList title="Responsabilidades" items={job.responsibilities} />
              <JobList title="Requisitos" items={job.requirements} />
              <JobList title="Diferenciais" items={job.differentials} />
            </div>

            <p className="job-card__closing">{job.closing}</p>
            <button type="button" className="job-card__button" onClick={() => openApplication(job)}>
              Candidatar-se
            </button>
          </article>
        ))}
      </div>

      {selectedJob && (
        <div className="application-overlay" role="presentation" onMouseDown={closeApplication}>
          <div
            className="application-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="application-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button type="button" className="application-modal__close" onClick={closeApplication} aria-label="Fechar formulário">
              ×
            </button>
            <span className="work-with-us__eyebrow">Sua próxima oportunidade</span>
            <h2 id="application-title">Candidatura</h2>
            <p className="application-modal__job">{selectedJob.title}</p>
            <form onSubmit={handleSubmit} noValidate>
              <div className="application-form__row">
                <label className={hasAttemptedSubmit && !formData.name.trim() ? "field--invalid" : ""}>
                  Nome completo*
                  <input name="name" value={formData.name} onChange={handleChange} aria-invalid={hasAttemptedSubmit && !formData.name.trim()} required />
                </label>
                <label className={hasAttemptedSubmit && !emailIsValid(formData.email) ? "field--invalid" : ""}>
                  E-mail (opcional)
                  <input type="email" name="email" value={formData.email} onChange={handleChange} aria-invalid={hasAttemptedSubmit && !emailIsValid(formData.email)} />
                  {hasAttemptedSubmit && formData.email && !emailIsValid(formData.email) && (
                    <span className="form-error">Informe um e-mail válido.</span>
                  )}
                </label>
              </div>
              <div className="application-form__row">
                <label className={hasAttemptedSubmit && formData.phone.replace(/\D/g, "").length !== 11 ? "field--invalid" : ""}>
                  Telefone*
                  <input type="tel" name="phone" value={formData.phone} onChange={handlePhoneChange} aria-invalid={hasAttemptedSubmit && formData.phone.replace(/\D/g, "").length !== 11} required />
                  {hasAttemptedSubmit && formData.phone.replace(/\D/g, "").length !== 11 && (
                    <span className="form-error">Informe um telefone válido.</span>
                  )}
                </label>
                <label className={hasAttemptedSubmit && !formData.city.trim() ? "field--invalid" : ""}>
                  Cidade/UF*
                  <input name="city" value={formData.city} onChange={handleChange} aria-invalid={hasAttemptedSubmit && !formData.city.trim()} required />
                </label>
              </div>
              <label className={hasAttemptedSubmit && !formData.experience.trim() ? "field--invalid" : ""}>
                Conte brevemente sobre sua experiência*
                <textarea name="experience" value={formData.experience} onChange={handleChange} aria-invalid={hasAttemptedSubmit && !formData.experience.trim()} rows={4} required />
              </label>
              <label>
                LinkedIn, portfólio ou mensagem (opcional)
                <textarea name="message" value={formData.message} onChange={handleChange} rows={3} />
              </label>
              <button type="submit" className="application-form__submit" disabled={isSending}>
                {isSending ? "Enviando..." : "Enviar candidatura"}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

const JobList = ({ title, items }: { title: string; items: string[] }) => (
  <div className="job-card__list">
    <h3>{title}</h3>
    <ul>
      {items.map((item) => <li key={item}>{item}</li>)}
    </ul>
  </div>
);

export default WorkWithUs;
